import { StatusTrackingEnum } from "@/enums"

/**
 * Tom semântico do status, na paleta do portal: emerald = desfecho bom,
 * amber = aguardando, red = falha, blue = movimento de estorno/reversão,
 * gray = encerrado sem pagamento.
 */
export type StatusTone =
  | "success"
  | "pending"
  | "error"
  | "info"
  | "neutral"

interface StatusTrackingConfig {
  labelKey: string
  /** Cor cheia — usada só nos marcadores da linha do tempo. */
  bgColor: string
  tone: StatusTone
}

/** Badge suave: fundo 50, borda 200, texto 700 (padrão de badge do portal). */
export const STATUS_TONE_CLASSES: Record<
  StatusTone,
  string
> = {
  success:
    "bg-emerald-50 border-emerald-200 text-emerald-700",
  pending: "bg-amber-50 border-amber-200 text-amber-700",
  error: "bg-red-50 border-red-200 text-red-700",
  info: "bg-blue-50 border-blue-200 text-blue-700",
  neutral: "bg-gray-50 border-gray-200 text-gray-700",
}

const FALLBACK_CONFIG: StatusTrackingConfig = {
  labelKey: "other",
  bgColor: "bg-gray-400",
  tone: "neutral",
}

/**
 * Config visual de cada status detalhado do pedido (o que o backend chama de
 * `status.status`). Módulo puro — sem React nem api — para ser reusado pelo
 * badge, pela linha do tempo e pelo detalhe, e testado isoladamente.
 */
export const statusTrackingConfig: Record<
  StatusTrackingEnum,
  StatusTrackingConfig
> = {
  [StatusTrackingEnum.PENDING]: {
    labelKey: "pending",
    bgColor: "bg-orange-400",
    tone: "pending",
  },
  [StatusTrackingEnum.FUTURE]: {
    labelKey: "future",
    bgColor: "bg-amber-400",
    tone: "pending",
  },
  [StatusTrackingEnum.PRE_AUTHORIZATION_WAITING]: {
    labelKey: "preAuthorizationWaiting",
    bgColor: "bg-yellow-400",
    tone: "pending",
  },
  [StatusTrackingEnum.PRE_AUTHORIZATION_ERROR]: {
    labelKey: "preAuthorizationError",
    bgColor: "bg-red-500",
    tone: "error",
  },
  [StatusTrackingEnum.PRE_AUTHORIZATION_TIMEOUT_ERROR]: {
    labelKey: "preAuthorizationTimeoutError",
    bgColor: "bg-red-500",
    tone: "error",
  },
  [StatusTrackingEnum.PRE_AUTHORIZED_BY_GATEWAY]: {
    labelKey: "preAuthorizedByGateway",
    bgColor: "bg-blue-400",
    tone: "info",
  },
  [StatusTrackingEnum.WAITING_PAYMENT]: {
    labelKey: "waitingPayment",
    bgColor: "bg-yellow-400",
    tone: "pending",
  },
  [StatusTrackingEnum.WAITING_PARTIAL_PAYMENT]: {
    labelKey: "waitingPartialPayment",
    bgColor: "bg-yellow-400",
    tone: "pending",
  },
  [StatusTrackingEnum.WAITING_PAYMENT_PIX]: {
    labelKey: "waitingPaymentPix",
    bgColor: "bg-cyan-400",
    tone: "pending",
  },
  [StatusTrackingEnum.WAITING_PAYMENT_BANK_SLIP]: {
    labelKey: "waitingPaymentBankSlip",
    bgColor: "bg-amber-500",
    tone: "pending",
  },
  [StatusTrackingEnum.WAITING_PAYMENT_BOLEPIX]: {
    labelKey: "waitingPaymentBolepix",
    bgColor: "bg-cyan-400",
    tone: "pending",
  },
  [StatusTrackingEnum.WAITING_PAYMENT_DIRECT_DEBIT]: {
    labelKey: "waitingPaymentDirectDebit",
    bgColor: "bg-sky-400",
    tone: "pending",
  },
  [StatusTrackingEnum.WAITING_PAYMENT_DIRECT_DEBIT_SCHEDULED]:
    {
      labelKey: "waitingPaymentDirectDebitScheduled",
      bgColor: "bg-sky-400",
      tone: "pending",
    },
  [StatusTrackingEnum.AUTHORIZATION_TIMEOUT_ERROR]: {
    labelKey: "authorizationTimeoutError",
    bgColor: "bg-red-500",
    tone: "error",
  },
  [StatusTrackingEnum.PAYMENT_IDENTIFIED]: {
    labelKey: "paymentIdentified",
    bgColor: "bg-green-400",
    tone: "success",
  },
  [StatusTrackingEnum.CONFIRMED]: {
    labelKey: "confirmed",
    bgColor: "bg-green-500",
    tone: "success",
  },
  [StatusTrackingEnum.CANCELED_BY_GATEWAY]: {
    labelKey: "canceledByGateway",
    bgColor: "bg-red-500",
    tone: "neutral",
  },
  [StatusTrackingEnum.CANCELED_BY_STORE]: {
    labelKey: "canceledByStore",
    bgColor: "bg-red-700",
    tone: "neutral",
  },
  [StatusTrackingEnum.CANCELED_BY_CUSTOMER]: {
    labelKey: "canceledByCustomer",
    bgColor: "bg-red-700",
    tone: "neutral",
  },
  [StatusTrackingEnum.EXPIRED]: {
    labelKey: "expired",
    bgColor: "bg-gray-400",
    tone: "neutral",
  },
  [StatusTrackingEnum.SUBMITTED_FOR_REVERSED]: {
    labelKey: "submittedForReversed",
    bgColor: "bg-blue-300",
    tone: "info",
  },
  [StatusTrackingEnum.REVERSED]: {
    labelKey: "reversed",
    bgColor: "bg-neutral-400",
    tone: "info",
  },
  [StatusTrackingEnum.REVERSED_FAILED]: {
    labelKey: "reversedFailed",
    bgColor: "bg-red-500",
    tone: "error",
  },
  [StatusTrackingEnum.SUBMITTED_FOR_REFUND]: {
    labelKey: "submittedForRefund",
    bgColor: "bg-blue-300",
    tone: "info",
  },
  [StatusTrackingEnum.REFUNDED]: {
    labelKey: "refunded",
    bgColor: "bg-green-400",
    tone: "info",
  },
  [StatusTrackingEnum.PARTIAL_REFUNDED]: {
    labelKey: "partialRefunded",
    bgColor: "bg-green-300",
    tone: "info",
  },
  [StatusTrackingEnum.REFUND_FAILED]: {
    labelKey: "refundFailed",
    bgColor: "bg-red-500",
    tone: "error",
  },
  [StatusTrackingEnum.REFUND_PENDING]: {
    labelKey: "refundPending",
    bgColor: "bg-blue-400",
    tone: "pending",
  },
  [StatusTrackingEnum.REFUND_CANCELED]: {
    labelKey: "refundCanceled",
    bgColor: "bg-gray-400",
    tone: "neutral",
  },
  [StatusTrackingEnum.ERROR]: {
    labelKey: "error",
    bgColor: "bg-red-500",
    tone: "error",
  },
}

/**
 * Config do status detalhado, com fallback seguro para valor desconhecido
 * (status novo no backend não pode quebrar a listagem).
 */
export const getStatusConfig = (
  status?: string
): StatusTrackingConfig =>
  statusTrackingConfig[status as StatusTrackingEnum] ??
  FALLBACK_CONFIG

/** Classes do badge suave correspondentes ao status detalhado. */
export const getStatusToneClasses = (status?: string) =>
  STATUS_TONE_CLASSES[getStatusConfig(status).tone]

/** Chave i18n do rótulo do status detalhado. */
export const getStatusLabelKey = (status?: string) =>
  `orders.statusTracking.orderStatus.${getStatusConfig(status).labelKey}`
